import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  listIncomeEntries,
  createIncomeEntry,
  deleteIncomeEntry,
  SOURCE_LABELS,
  PAYMENT_METHOD_LABELS,
  type CreateIncomeInput,
  type IncomeRecord,
} from '../../services/accounting';
import { listRestaurants, type RestaurantRecord } from '../../services/restaurants';

const currency = (v: number) => `₺${v.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;

const todayStr = () => new Date().toISOString().slice(0, 10);

const SOURCES = Object.entries(SOURCE_LABELS);
const METHODS = Object.entries(PAYMENT_METHOD_LABELS);

export default function IncomeManager() {
  const qc = useQueryClient();

  // Filters
  const [filterRestaurant, setFilterRestaurant] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterMethod, setFilterMethod] = useState('');

  // Form
  const [showForm, setShowForm] = useState(false);
  const [formRestaurant, setFormRestaurant] = useState('');
  const [formDate, setFormDate] = useState(todayStr());
  const [formSource, setFormSource] = useState('');
  const [formMethod, setFormMethod] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formDescription, setFormDescription] = useState('');

  // Data
  const { data: restaurants = [] } = useQuery<RestaurantRecord[]>({
    queryKey: ['restaurants'],
    queryFn: listRestaurants,
  });

  const {
    data: entries = [],
    isLoading,
    error,
  } = useQuery<IncomeRecord[]>({
    queryKey: ['accounting', 'income', filterRestaurant, filterDateFrom, filterDateTo],
    queryFn: () =>
      listIncomeEntries(
        filterRestaurant || undefined,
        filterDateFrom || undefined,
        filterDateTo || undefined,
      ),
  });

  const filtered = useMemo(() => {
    let result = entries;
    if (filterSource) result = result.filter((e) => e.source === filterSource);
    if (filterMethod) result = result.filter((e) => e.paymentMethod === filterMethod);
    return result;
  }, [entries, filterSource, filterMethod]);

  const totalFiltered = useMemo(
    () => filtered.reduce((s, e) => s + e.amount, 0),
    [filtered],
  );

  // Group by payment method for quick insight
  const methodTotals = useMemo(() => {
    const map = new Map<string, number>();
    filtered.forEach((e) => {
      const label = PAYMENT_METHOD_LABELS[e.paymentMethod] ?? e.paymentMethod;
      map.set(label, (map.get(label) ?? 0) + e.amount);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [filtered]);

  const sourceTotals = useMemo(() => {
    const map = new Map<string, number>();
    filtered.forEach((e) => {
      const label = SOURCE_LABELS[e.source] ?? e.source;
      map.set(label, (map.get(label) ?? 0) + e.amount);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [filtered]);

  // Mutations
  const addMutation = useMutation({
    mutationFn: (input: CreateIncomeInput) => createIncomeEntry(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounting', 'income'] });
      setShowForm(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteIncomeEntry(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounting', 'income'] });
    },
  });

  function resetForm() {
    setFormAmount('');
    setFormDate(todayStr());
    setFormDescription('');
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formRestaurant || !formSource || !formMethod || !formAmount) return;
    addMutation.mutate({
      restaurantId: formRestaurant,
      incomeDate: formDate,
      source: formSource,
      paymentMethod: formMethod,
      amount: parseFloat(formAmount),
      description: formDescription || undefined,
    });
  }

  return (
    <>
      {/* Filters */}
      <div className="card compact">
        <div className="card-header">
          <p className="card-title">Filtreler</p>
          <button
            type="button"
            className="button primary"
            onClick={() => setShowForm((v) => !v)}
          >
            {showForm ? 'Formu Kapat' : '+ Yeni Gelir'}
          </button>
        </div>
        <div className="form-grid three-columns">
          <div className="form-field">
            <label>Restoran</label>
            <select value={filterRestaurant} onChange={(e) => setFilterRestaurant(e.target.value)}>
              <option value="">Tümü</option>
              {restaurants.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>Kaynak</label>
            <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)}>
              <option value="">Tümü</option>
              {SOURCES.map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>Ödeme Yöntemi</label>
            <select value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)}>
              <option value="">Tümü</option>
              {METHODS.map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>Başlangıç Tarihi</label>
            <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />
          </div>
          <div className="form-field">
            <label>Bitiş Tarihi</label>
            <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Add Form */}
      {showForm && (
        <div className="card">
          <p className="card-title">Yeni Gelir Ekle</p>
          <form onSubmit={handleSubmit}>
            <div className="form-grid two-columns">
              <div className="form-field">
                <label>Restoran *</label>
                <select value={formRestaurant} onChange={(e) => setFormRestaurant(e.target.value)} required>
                  <option value="">Seçin</option>
                  {restaurants.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Tarih *</label>
                <input
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  required
                />
              </div>
              <div className="form-field">
                <label>Kaynak / Platform *</label>
                <select value={formSource} onChange={(e) => setFormSource(e.target.value)} required>
                  <option value="">Seçin</option>
                  {SOURCES.map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Ödeme Yöntemi *</label>
                <select value={formMethod} onChange={(e) => setFormMethod(e.target.value)} required>
                  <option value="">Seçin</option>
                  {METHODS.map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Tutar (₺) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                  required
                />
              </div>
              <div className="form-field">
                <label>Açıklama</label>
                <input
                  type="text"
                  placeholder="Opsiyonel not"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                />
              </div>
            </div>
            <div className="form-actions" style={{ marginTop: '1rem' }}>
              <button type="button" className="button ghost" onClick={() => { setShowForm(false); resetForm(); }}>
                İptal
              </button>
              <button
                type="submit"
                className="button primary"
                disabled={addMutation.isPending}
              >
                {addMutation.isPending ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
            {addMutation.isError && (
              <p className="error-text" style={{ marginTop: '0.5rem' }}>
                {addMutation.error instanceof Error ? addMutation.error.message : 'Kayıt sırasında hata oluştu.'}
              </p>
            )}
          </form>
        </div>
      )}

      {/* Summary cards */}
      <div className="insight-grid">
        <div className="insight-card">
          <span>Toplam Gelir</span>
          <strong>{currency(totalFiltered)}</strong>
        </div>
        <div className="insight-card">
          <span>Kayıt Sayısı</span>
          <strong>{filtered.length}</strong>
        </div>
        {methodTotals.map(([method, total]) => (
          <div className="insight-card" key={method}>
            <span>{method}</span>
            <strong>{currency(total)}</strong>
          </div>
        ))}
      </div>

      {/* Source breakdown */}
      {sourceTotals.length > 0 && (
        <div className="card compact">
          <p className="card-title">Kaynağa Göre Dağılım</p>
          <div className="chip-row" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
            {sourceTotals.map(([source, total]) => (
              <span key={source} className="chip neutral" style={{ fontSize: '0.85rem', padding: '0.45rem 0.85rem' }}>
                {source}: <strong style={{ marginLeft: '0.25rem' }}>{currency(total)}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card">
        <p className="card-title">Gelir Kayıtları</p>
        {isLoading ? (
          <div className="empty-state">
            <p>Yükleniyor...</p>
          </div>
        ) : error ? (
          <div className="empty-state">
            <strong>Veriler yüklenemedi</strong>
            <p>{error instanceof Error ? error.message : 'Bilinmeyen hata'}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <strong>Henüz gelir kaydı yok</strong>
            <p>Yeni gelir eklemek için yukarıdaki butonu kullanın.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Restoran</th>
                  <th>Kaynak</th>
                  <th>Ödeme</th>
                  <th style={{ textAlign: 'right' }}>Tutar</th>
                  <th>Açıklama</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((entry) => (
                  <tr key={entry.id}>
                    <td>{new Date(entry.incomeDate).toLocaleDateString('tr-TR')}</td>
                    <td>{entry.restaurantName}</td>
                    <td>
                      <span className="chip success">
                        {SOURCE_LABELS[entry.source] ?? entry.source}
                      </span>
                    </td>
                    <td>
                      <span className="chip neutral">
                        {PAYMENT_METHOD_LABELS[entry.paymentMethod] ?? entry.paymentMethod}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{currency(entry.amount)}</td>
                    <td style={{ fontSize: '0.85rem', color: '#64748b' }}>{entry.description ?? '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="text-button danger"
                        disabled={deleteMutation.isPending}
                        onClick={() => {
                          if (confirm('Bu gelir kaydını silmek istediğinize emin misiniz?')) {
                            deleteMutation.mutate(entry.id);
                          }
                        }}
                      >
                        Sil
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
